import React, { memo, useState, forwardRef, useImperativeHandle, useRef, useEffect, useMemo } from 'react';

import { createRoot, Root } from 'react-dom/client';

import { Icon as LegacyIcon } from '@ant-design/compatible';

import { Modal, Button } from 'antd';

import { ButtonType } from 'antd/es/button/button';

import Dialog from 'rc-dialog';

import styles from './index.less';

/*
 * 为了兼容部分电脑modal.confirm 导致页面崩溃的问题
 *  由于在用户电脑测试 正常的模态框可以显示并且没有卡死的问题 所以需要使用正常的模态框 模拟 modal.confirm
 *  测试用户的情况需要兼容三种情况
 *    1. 用户可以直接使用rc-dialog
 *    2. 用户不能直接使用, 直接使用modal进行魔改
 *    3. 手写confirm
 *    添加全局变量进行测试window.__modal_confirm__
 *        1. 1 使用rc-dialog
 *        2. 2 使用modal
 *        3. 3 使用手写confirm
 */

interface IProps {
  width?: number;
  title?: string;
  content?: string;
  showModal?: boolean;
  onOk?: (...args: any[]) => void;
  onCancel?: (...args: any[]) => void;
  onOktext?: string;
  onCanceltext?: string;
  bodyStyle?: React.CSSProperties;
  centered?: boolean;
  okType?: ButtonType;
  icon?: React.ReactNode | boolean | string;
}
interface ConfirmProps extends IProps {
  afterClose?: () => void;
  close: (...args: any[]) => void;
  autoFocusButton?: null | 'ok' | 'cancel';
}

const destroyFns: Array<() => void> = [];

const renderIcon = (icon: React.ReactNode | boolean | string) => {
  const myIcon = icon === undefined ? 'question-circle' : icon;

  return typeof myIcon === 'string' ? <LegacyIcon type={myIcon} style={{ color: '#faad14', fontSize: '22px' }} /> : icon;
};

const AntdModalConfirm: React.FC<ConfirmProps> = ({
  title = '',
  content = '',
  width = 416,
  showModal = true,
  centered = false,
  bodyStyle,
  onOktext = '确定',
  onCanceltext = '取消',
  onOk,
  onCancel,
  okType = 'primary',
  icon,
  close,
  afterClose
}) => {
  const cancelButton = (
    <Button autoFocus={false} onClick={() => { onCancel?.(); close(); }}>
      {onCanceltext}
    </Button>
  );

  const okButton = (
    <Button type={okType} autoFocus={true} onClick={() => { onOk?.(); close(); }}>
      {onOktext}
    </Button>
  );

  return (
    <Modal
      title={null}
      open={showModal}
      footer={null}
      width={width}
      onCancel={() => close({ triggerCancel: true })}
      closable={false}
      afterClose={afterClose}
      maskClosable={false}
      centered={centered}
      bodyStyle={bodyStyle ?? {}}>
      <div className={styles.myconfirmbody} id="confirmModal-Confirm">
        {renderIcon(icon)}
        {title ? <div className={styles.myconfirmtitle}>{title}</div> : null}
        {content ? <div className={styles.myconfirmcontent}>{content}</div> : null}
      </div>
      <div className={styles.btnGroup}>
        {cancelButton}
        {okButton}
      </div>
    </Modal>
  );
};

const DialogConfirm: React.FC<IProps> = props => {
  return <Dialog visible={true} />;
};

class AntdModal extends Modal {
  static AntdModalConfirmFunc: (props: ConfirmProps) => void;
  static AntdModalConfirmRender: React.FC<IProps>;
}

const AntdModalConfirmFunc = (props: ConfirmProps) => {
  const div = document.createElement('div');
  document.body.appendChild(div);
  const root: Root = createRoot(div);

  const close = (...args: any[]) => {
    currentConfig = {
      ...currentConfig,
      showModal: false,
      afterClose: destroy.bind(this, ...args)
    };
    render(currentConfig);
  };

  let currentConfig = { ...props, close, visible: true } as any;

  const destroy = (...args: any[]) => {
    root.unmount();
    if (div.parentNode) {
      div.parentNode.removeChild(div);
    }
    const triggerCancel = args.some(param => param && param.triggerCancel);

    if (props.onCancel && triggerCancel) {
      props.onCancel(...args);
    }
    for (let i = 0; i < destroyFns.length; i++) {
      const fn = destroyFns[i];
      // eslint-disable-next-line no-use-before-define
      if (fn === close) {
        destroyFns.splice(i, 1);
        break;
      }
    }
  };

  const render = (innerProps: ConfirmProps) => {
    root.render(<AntdModalConfirm {...innerProps} />);
  };

  render(currentConfig);

  destroyFns.push(close);

  return {
    destroy: close
  };
};

const AntdModalConfirmRender = (props: IProps) => {
  const {
    showModal,
    width,
    onOk,
    onCancel,
    onOktext = '确定',
    onCanceltext = '取消',
    bodyStyle,
    icon,
    title,
    content,
    okType = 'primary',
    centered = false
  } = props;

  const close = onCancel ? onCancel : () => {};

  const cancelButton = (
    <Button autoFocus={false} onClick={() => { onCancel?.(); close(); }}>
      {onCanceltext}
    </Button>
  );

  const okButton = (
    <Button type={okType} autoFocus={true} onClick={() => { onOk?.(); close(); }}>
      {onOktext}
    </Button>
  );
  return (
    <Modal
      width={width}
      footer={null}
      centered={centered}
      open={showModal}
      title={null}
      closable={false}
      maskClosable={false}
      onCancel={onCancel}
      bodyStyle={bodyStyle ?? {}}>
      <div className={styles.myconfirmbody} id="confirmModal-Modal">
        {renderIcon(icon)}
        {title ? <div className={styles.myconfirmtitle}>{title}</div> : null}
        {content ? <div className={styles.myconfirmcontent}>{content}</div> : null}
      </div>
      <div className={styles.btnGroup}>
        {cancelButton}
        {okButton}
      </div>
    </Modal>
  );
};

AntdModal.AntdModalConfirmFunc = AntdModalConfirmFunc;
AntdModal.AntdModalConfirmRender = AntdModalConfirmRender;

export default AntdModal;
